// A field's allowed values (feature 004).
//
// A field is a *choice field* when it carries a non-empty ordered set of allowed
// values; at fill time such a field is a dropdown rather than a free-text box.
// This module owns the two rules that keep that data honest, shared by the
// browser (immediate authoring feedback) and the Lambda (the trust boundary) so
// the two cannot disagree (plan Complexity Tracking):
//
//   - parseFieldOptions     — what makes a value acceptable (reject, never repair)
//   - reconcileFieldOptions — which fields may hold a set (the document decides)
//
// Like the rest of this package it imports nothing from AWS or the DOM.

import { PLACEHOLDER_NAME_MAX_LENGTH, PLACEHOLDER_NAME_PATTERN, type ValidationIssue } from './schema.js';

/** Field name → ordered, de-duplicated, non-empty list of allowed values. */
export type FieldOptions = Record<string, string[]>;

/** A dropdown is for a small fixed set; these bounds cap item size (D-07). */
export const MAX_OPTIONS_PER_FIELD = 50;
/** Matches MAX_TITLE_LENGTH — a value already deemed a reasonable single line. */
export const MAX_OPTION_LENGTH = 200;

export type ParseFieldOptionsResult =
  | { ok: true; value: FieldOptions }
  | { ok: false; issues: ValidationIssue[] };

/**
 * Validate untrusted input into a {@link FieldOptions}.
 *
 * On any violation the result carries `issues` and no `value` — there is
 * deliberately no "cleaned" set to accidentally persist, mirroring
 * `parseTemplateDocument`. On success every value is trimmed, order preserved,
 * keys are valid placeholder names, and values are non-blank, pairwise distinct,
 * and within bounds. `parseFieldOptions({})` succeeds with `{}`.
 */
export function parseFieldOptions(input: unknown): ParseFieldOptionsResult {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    return { ok: false, issues: [{ path: '(root)', message: 'fieldOptions must be an object' }] };
  }

  const issues: ValidationIssue[] = [];
  const out: FieldOptions = {};

  for (const [name, rawValues] of Object.entries(input as Record<string, unknown>)) {
    if (name.length > PLACEHOLDER_NAME_MAX_LENGTH || !PLACEHOLDER_NAME_PATTERN.test(name)) {
      issues.push({ path: name, message: 'field name is not a valid placeholder name' });
      continue;
    }
    if (!Array.isArray(rawValues)) {
      issues.push({ path: name, message: 'allowed values must be an array' });
      continue;
    }
    if (rawValues.length === 0) {
      issues.push({ path: name, message: 'a field with allowed values must have at least one' });
      continue;
    }
    if (rawValues.length > MAX_OPTIONS_PER_FIELD) {
      issues.push({ path: name, message: `at most ${MAX_OPTIONS_PER_FIELD} values are allowed` });
      continue;
    }

    const values: string[] = [];
    const seen = new Set<string>();
    let fieldOk = true;

    rawValues.forEach((raw, index) => {
      const path = `${name}.${index}`;
      if (typeof raw !== 'string') {
        issues.push({ path, message: 'allowed value must be a string' });
        fieldOk = false;
        return;
      }
      const value = raw.trim();
      if (value.length === 0) {
        issues.push({ path, message: 'allowed value must not be blank' });
        fieldOk = false;
        return;
      }
      if (value.length > MAX_OPTION_LENGTH) {
        issues.push({ path, message: `allowed value must be at most ${MAX_OPTION_LENGTH} characters` });
        fieldOk = false;
        return;
      }
      if (seen.has(value)) {
        issues.push({ path, message: 'duplicate allowed value' });
        fieldOk = false;
        return;
      }
      seen.add(value);
      values.push(value);
    });

    if (fieldOk) out[name] = values;
  }

  if (issues.length > 0) return { ok: false, issues };
  return { ok: true, value: out };
}

/**
 * Keep only the option sets whose field name still exists in the document.
 *
 * `names` is the body-derived field list (`extractPlaceholderNames`), the single
 * source of truth for which fields exist. Options for a name the document does
 * not contain are dropped, not an error (D-03): deleting a field's last
 * occurrence removes its set on the next save with no save-blocking failure.
 * Pure and total; never throws.
 */
export function reconcileFieldOptions(
  options: FieldOptions,
  names: readonly string[],
): FieldOptions {
  const present = new Set(names);
  const out: FieldOptions = {};
  for (const [name, values] of Object.entries(options)) {
    if (present.has(name)) out[name] = [...values];
  }
  return out;
}
